import { ExpenseClaim } from '../../types/expense';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { format } from 'date-fns';
import { Receipt, Plane, Utensils, Package, PartyPopper, Bed, Fuel, ParkingCircle, MoreHorizontal } from 'lucide-react';
import { base64ToDataUrl } from '../../utils/image';
import { useApiService } from '../../hooks/use-api-service';
import { getReceiptTypeConfig } from '@/config/receipt-types';
import { formatCurrency } from '@/lib/currency';
import { EditExpenseClaimDialog } from '../expense-claims/edit-expense-claim-dialog';
import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { receiptTypeService, ReceiptTypeDto } from '@/services/receiptTypeService';
import { useDataMode } from '@/providers/DataModeProvider';

interface ExpenseClaimsListProps {
  expenses: ExpenseClaim[];
  isLoading?: boolean;
}

export function ExpenseClaimsList({ expenses, isLoading = false }: ExpenseClaimsListProps) {
  const { api } = useApiService();
  const queryClient = useQueryClient();
  const { isLiveMode } = useDataMode();
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<ExpenseClaim | null>(null);
  const [typesById, setTypesById] = useState<Record<string, ReceiptTypeDto>>({});
  const [isLoadingTypes, setIsLoadingTypes] = useState(true);

  const updateExpenseMutation = useMutation({
    mutationFn: (expense: ExpenseClaim) => api.updateExpenseClaim(expense),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['expenseClaims'] });
      setEditDialogOpen(false);
      toast.success('Expense claim updated successfully');
    },
    onError: () => {
      toast.error('Failed to update expense claim');
    },
  });

  const handleRowClick = (expense: ExpenseClaim) => {
    setSelectedExpense(expense);
    setEditDialogOpen(true);
  };

  const handleSaveExpense = (expense: ExpenseClaim) => {
    updateExpenseMutation.mutate(expense);
  };

  useEffect(() => {
    const fetchReceiptTypes = async () => {
      try {
        setIsLoadingTypes(true);
        const types = await receiptTypeService.getAll(isLiveMode);
        const typesMap = Object.fromEntries(types.map((t: ReceiptTypeDto) => [t.id, t]));
        setTypesById(typesMap);
      } catch (error) {
        console.error('Failed to fetch receipt types:', error);
        setTypesById({});
      } finally {
        setIsLoadingTypes(false);
      }
    };

    fetchReceiptTypes();
  }, [isLiveMode]);

  
  if (isLoading) {
    return (
      <Card>
        <div className="p-6 space-y-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-center gap-4">
              <Skeleton className="h-12 w-12 rounded" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-1/4" />
                <Skeleton className="h-3 w-1/3" />
              </div>
              <Skeleton className="h-6 w-20" />
            </div>
          ))}
        </div>
      </Card>
    );
  }

  if (expenses.length === 0) {
    return (
      <Card className="p-12 flex flex-col items-center justify-center text-center">
        <Receipt className="h-12 w-12 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">No expense claims</h3>
        <p className="text-sm text-muted-foreground">
          Process receipts to create expense claims
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Receipt</TableHead>
            <TableHead>Type</TableHead>
            <TableHead>Supplier</TableHead>
            <TableHead>Date</TableHead>
            <TableHead className="text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {expenses.map((expense) => {
            const typeConfig = typesById[expense.expenseType] || getReceiptTypeConfig(expense.expenseType);
            const typeLabel = typeConfig 
              ? (typeConfig.name === 'OfficeSupplies' ? 'Office Supplies' : typeConfig.name)
              : 'Unknown';
            
            return (
              <TableRow 
                key={expense.id} 
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleRowClick(expense)}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded overflow-hidden bg-muted">
                      <img
                        src={base64ToDataUrl(expense.receiptImageData)}
                        alt="Receipt"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                </TableCell>
                <TableCell>
                  {typeConfig ? (
                    <Badge 
                      className={`${typeConfig.color} ${typeConfig.textColor} border-0`}
                    >
                      {typeLabel}
                    </Badge>
                  ) : (
                    <span className="font-medium">{typeLabel}</span>
                  )}
                </TableCell>
                <TableCell>
                  <div>
                    <div className="font-medium">{expense.supplier}</div>
                    {expense.description && (
                      <div className="text-xs text-muted-foreground">
                        {expense.description}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell>
                  {format(new Date(expense.transactionDate), 'dd MMM yyyy')}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {formatCurrency(expense.amount, expense.currency)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Edit Dialog */}
      {selectedExpense && (
        <EditExpenseClaimDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          expense={selectedExpense}
          onSave={handleSaveExpense}
        />
      )}
    </Card>
  );
}
